import React from 'react';
import { v4 } from 'uuid';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useDrag, useDrop } from 'react-dnd';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Controller } from 'react-hook-form';
import type { Control } from 'react-hook-form';

const DND_TYPE_HAMBURGER_ITEM = 'hamburgerMenuItem';
const NEW_CONVERSATION_ID = 'new-conversation';

const moveIndex = <T,>(list: T[], from: number, to: number): T[] => {
  const copy = list.slice();
  const [moved] = copy.splice(from, 1);
  copy.splice(to, 0, moved);
  return copy;
};

export interface HamburgerMenuLink {
  id?: string;
  title: string;
  url: string;
}

/** Single item in the hamburger menu: either a link or the New Conversation action */
export type HamburgerMenuItem =
  | { type: 'link'; id: string; title: string; url: string }
  | { type: 'newConversation'; enabled: boolean };

/** Normalized format: single ordered list of items */
export interface HamburgerMenuItemsValue {
  items: HamburgerMenuItem[];
}

/** Legacy format (backward compat) */
export interface HamburgerMenuValue {
  links?: HamburgerMenuLink[];
  enableNewConversation?: boolean;
  items?: HamburgerMenuItem[];
}

function normalizeToItems(value: HamburgerMenuValue | undefined): HamburgerMenuItem[] {
  if (Array.isArray(value?.items) && value.items.length > 0) {
    return value.items;
  }
  const links = Array.isArray(value?.links) ? value.links : [];
  const enableNewConversation = value?.enableNewConversation !== false;
  const linkItems: HamburgerMenuItem[] = links.map((l) => ({
    type: 'link' as const,
    id: (l as { id?: string }).id || v4(),
    title: l.title || '',
    url: l.url || '',
  }));
  const newConv: HamburgerMenuItem = { type: 'newConversation', enabled: enableNewConversation };
  return [newConv, ...linkItems];
}

function itemsToValue(items: HamburgerMenuItem[]): HamburgerMenuItemsValue {
  return { items };
}

interface HamburgerLinkRowProps {
  link: Extract<HamburgerMenuItem, { type: 'link' }>;
  index: number;
  items: HamburgerMenuItem[];
  onChange: (items: HamburgerMenuItem[]) => void;
  onMove: (from: number, to: number) => void;
  disabled?: boolean;
}

const HamburgerLinkRow = ({
  link,
  index,
  items,
  onChange,
  onMove,
  disabled,
}: HamburgerLinkRowProps) => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [, drop] = useDrop({
    accept: DND_TYPE_HAMBURGER_ITEM,
    hover: (item: { index: number }, monitor) => {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      const hoverRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverRect.bottom - hoverRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverClientY = clientOffset.y - hoverRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      onMove(dragIndex, hoverIndex);
      // eslint-disable-next-line no-param-reassign
      item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: DND_TYPE_HAMBURGER_ITEM,
    item: { index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });
  drag(drop(ref));

  const handleChange = (field: 'title' | 'url', val: string) => {
    const next = items.slice();
    const current = next[index];
    if (current.type === 'link') {
      next[index] = { ...current, [field]: val };
    }
    onChange(next);
  };

  const handleDelete = () => {
    const linkItems = items.filter((i): i is Extract<HamburgerMenuItem, { type: 'link' }> => i.type === 'link');
    if (linkItems.length <= 1) return;
    const next = items.filter((_, i) => i !== index);
    onChange(next);
  };

  return (
    <Stack
      ref={ref}
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{ mb: 1, opacity: isDragging ? 0.6 : 1 }}
    >
      <Box sx={{ cursor: 'grab' }}>
        <DragIndicatorIcon />
      </Box>
      <TextField
        size="small"
        label="Title"
        value={link.title || ''}
        onChange={(e) => handleChange('title', e.target.value)}
        disabled={disabled}
        sx={{ flex: 1, minWidth: 120 }}
      />
      <TextField
        size="small"
        label="URL"
        value={link.url || ''}
        onChange={(e) => handleChange('url', e.target.value)}
        disabled={disabled}
        placeholder="https://"
        sx={{ flex: 2, minWidth: 180 }}
      />
      <Button
        size="small"
        onClick={handleDelete}
        disabled={disabled || items.filter((i) => i.type === 'link').length <= 1}
        sx={{ minWidth: 36 }}
      >
        <DeleteIcon fontSize="small" />
      </Button>
    </Stack>
  );
};

interface NewConversationRowProps {
  index: number;
  items: HamburgerMenuItem[];
  onChange: (items: HamburgerMenuItem[]) => void;
  onMove: (from: number, to: number) => void;
  disabled?: boolean;
}

const NewConversationRow = ({
  index,
  items,
  onChange,
  onMove,
  disabled,
}: NewConversationRowProps) => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const item = items[index];
  if (item.type !== 'newConversation') return null;
  const enabled = item.enabled;

  const [, drop] = useDrop({
    accept: DND_TYPE_HAMBURGER_ITEM,
    hover: (dragItem: { index: number }, monitor) => {
      if (!ref.current) return;
      const dragIndex = dragItem.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      const hoverRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverRect.bottom - hoverRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverClientY = clientOffset.y - hoverRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      onMove(dragIndex, hoverIndex);
      // eslint-disable-next-line no-param-reassign
      dragItem.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: DND_TYPE_HAMBURGER_ITEM,
    item: { index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });
  drag(drop(ref));

  const handleToggle = (checked: boolean) => {
    const next = items.slice();
    next[index] = { type: 'newConversation', enabled: checked };
    onChange(next);
  };

  return (
    <Stack
      ref={ref}
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{ mb: 1, opacity: isDragging ? 0.6 : 1 }}
    >
      <Box sx={{ cursor: 'grab' }}>
        <DragIndicatorIcon />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 120 }}>
        <RefreshIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />
        <Typography variant="body2" color="text.secondary">
          New Conversation
        </Typography>
      </Box>
      <Box sx={{ flex: 2 }} />
      <FormControlLabel
        control={(
          <Checkbox
            checked={enabled}
            onChange={(e) => handleToggle(e.target.checked)}
            disabled={disabled}
          />
        )}
        label="Show in menu"
      />
    </Stack>
  );
};

const HamburgerMenuLinksControl = (
  control: Control,
  fieldName: string,
  field: { readOnly?: boolean },
  readOnly: boolean,
) => (
  <Controller
    name={fieldName}
    control={control}
    defaultValue={{ items: [{ type: 'newConversation', enabled: true }] }}
    render={({
      field: { value, onChange },
    }: {
      field: {
        value: HamburgerMenuValue;
        onChange: (v: HamburgerMenuValue) => void;
      };
    }) => {
      const items = normalizeToItems(value);
      const disabled = field?.readOnly || readOnly;

      const handleItemsChange = (next: HamburgerMenuItem[]) => {
        onChange(itemsToValue(next));
      };

      const handleMove = (from: number, to: number) => {
        handleItemsChange(moveIndex(items, from, to));
      };

      const handleAdd = () => {
        const newLink: HamburgerMenuItem = {
          type: 'link',
          id: v4(),
          title: '',
          url: '',
        };
        handleItemsChange([...items, newLink]);
      };

      return (
        <Box
          sx={{
            border: '1px solid',
            borderColor: '#e0e0eb',
            borderRadius: 1,
            p: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Menu items (drag to reorder)
          </Typography>
          {items.map((item, i) =>
            item.type === 'newConversation' ? (
              <NewConversationRow
                key={NEW_CONVERSATION_ID}
                index={i}
                items={items}
                onChange={handleItemsChange}
                onMove={handleMove}
                disabled={disabled}
              />
            ) : (
              <HamburgerLinkRow
                key={item.id}
                link={item}
                index={i}
                items={items}
                onChange={handleItemsChange}
                onMove={handleMove}
                disabled={disabled}
              />
            ),
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              disabled={disabled}
            >
              Add link
            </Button>
            {!items.some((i) => i.type === 'newConversation') && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => handleItemsChange([{ type: 'newConversation', enabled: true }, ...items])}
                disabled={disabled}
              >
                Add New Conversation
              </Button>
            )}
          </Stack>
        </Box>
      );
    }}
  />
);

export default HamburgerMenuLinksControl;
